import { get } from "lodash";
import { Booking } from "../../types";
import { foundIn } from "../../utils";

export const BookingStatus = {
  NEW: "new",
  REVISED: "revised",
  ACCEPTED: "accepted",
  ALLOCATED: "allocated",
  DELIVERED: "delivered",
  SURGERY_STARTED: "surgery_started",
  REPORT_SUBMITTED: "report_submitted",
  INVOICED: "invoiced",
  TOPPED_UP: "topped_up",
  CANCELLED: "cancelled",
  INVOICED_TOPPED_UP: "invoiced_topped_up",
  CANCELLATION_REQUEST: "cancellation_request",
  NO_USAGE: "no_usage",
};

export const OrderActions = {
  REVISE: "revise",
  ACCEPT: "accept",
  CANCEL: "cancel",
  ALLOCATE: "allocate",
  CANCEL_REQUEST: "cancellation_request",
  FINISH_ALLOCATING: "finish_allocating",
  UNFINISH_ALLOCATING: "unfinish_allocating",
  FINISH_DELIVERING: "finish_delivering",
  START_SURGERY: "start_surgery",
  SUBMIT_REPORT: "submit_report",
  SEND_INVOICE: "send_invoice",
  TOP_UP: process.env.REACT_APP_COMPANY_CODE === "STMY" ? "returned" : "top_up",
  NO_USAGE: "cancel_no_usage",
};

export const StatusColor = {
  NEW: "#025F11", //same with User Active
  ACCEPTED: "#4E05DF",
  ALLOCATED: "#2196F3",
  DELIVERED: "#5F3F02", //same with Issue Open
  DEFAULT: "#3f51b5",
  INACTIVE: "#aaa",
};

export const can = (booking: Booking, action: string): boolean => {
  switch (action) {
    case OrderActions.ACCEPT:
      return foundIn(booking.status, [BookingStatus.NEW]);

    case OrderActions.REVISE:
      return foundIn(booking.status, [
        BookingStatus.NEW,
        BookingStatus.ACCEPTED,
        BookingStatus.DELIVERED,
      ]);

    case OrderActions.ALLOCATE:
      return foundIn(booking.status, [BookingStatus.ACCEPTED]);

    case OrderActions.FINISH_ALLOCATING:
      return foundIn(booking.status, [BookingStatus.ACCEPTED]);

    case OrderActions.FINISH_DELIVERING:
      return foundIn(booking.status, [BookingStatus.ALLOCATED]);

    case OrderActions.START_SURGERY:
      return foundIn(booking.status, [BookingStatus.DELIVERED]);

    case OrderActions.SUBMIT_REPORT:
      return foundIn(booking.status, [
        BookingStatus.ALLOCATED,
        BookingStatus.DELIVERED,
        BookingStatus.SURGERY_STARTED,
        BookingStatus.ACCEPTED,
      ]);

    case OrderActions.SEND_INVOICE:
      return foundIn(booking.status, [
        BookingStatus.REPORT_SUBMITTED,
        BookingStatus.TOPPED_UP,
      ]);

    case OrderActions.TOP_UP:
      return foundIn(booking.status, [
        BookingStatus.REPORT_SUBMITTED,
        BookingStatus.INVOICED,
      ]);

    case OrderActions.CANCEL_REQUEST:
      return foundIn(booking.status, [BookingStatus.CANCELLATION_REQUEST]);

    case OrderActions.CANCEL:
      return foundIn(booking.status, [
        BookingStatus.NEW,
        BookingStatus.ACCEPTED,
        BookingStatus.DELIVERED,
        BookingStatus.ALLOCATED,
      ]);

    case OrderActions.UNFINISH_ALLOCATING:
      return foundIn(booking.status, [BookingStatus.ALLOCATED]);

    case OrderActions.NO_USAGE:
      return foundIn(booking.status, [BookingStatus.NO_USAGE]);

    default:
      return false;
  }
};

export const IssueStatus = {
  OPEN: "open",
  RESOLVED: "resolved",
};

/**
 * Booking label text. EOT bookings use a compact vertical layout (combined names).
 * Non-EOT bookings keep the original layout: ID, surgeon, consultant, hospital each on their own lines.
 */
export const stringify = (booking: Booking) => {
  if (get(booking, "is_eot")) {
    const eotLine =
      process.env.REACT_APP_COMPANY_CODE === "STMY" ? "UD!" : "EOT!";
    const surgeon = String(get(booking, "surgeon.full_name", "") ?? "").trim();
    const consultant = String(
      get(booking, "consultant.full_name", "") ?? "",
    ).trim();
    const nameLine = [surgeon, consultant].filter(Boolean).join(", ");
    const hospital = String(get(booking, "hospital.name", "") ?? "").trim();
    const room = String(get(booking, "hospital_room.name", "") ?? "").trim();
    const locationLine =
      hospital && room
        ? `${hospital} (${room})`
        : hospital || room || "";

    const lines: string[] = [eotLine, `ID: ${booking.id}`];
    if (nameLine) {
      lines.push(nameLine);
    }
    if (locationLine) {
      lines.push(locationLine);
    }
    return lines.join("\n");
  }

  return `
  ID: ${booking.id}
${get(booking, "surgeon.full_name")}
${get(booking, "consultant.full_name")}
${get(booking, "hospital.name")} (${get(booking, "hospital_room.name")})
`;
};

export const stringifyHtml = (booking: Booking) => {
  return `<div>Hello</div>`;
};
